import ItiButton from "@/iti-core-react/components/ItiButton/ItiButton";
import ItiTextBoxComponent from "@/iti-core-react/components/ItiTextBox/ItiTextBox";
import { useLanguage } from "@/iti-core-react/hooks/useLanguage";
import CatalogueDataGridFilter from "@/pages/Catalogue/components/CatalogueSearchOptions/CatalogueDataGridFilter/CatalogueDataGridFilter";
import { AutomationStatus } from "@/shared/enums/automation-status";
import {
	CatalogueSearchFilterType,
	SortDirection,
} from "@/shared/enums/catalogue-search-filter-type";
import { StreamingSortByEnum } from "@/shared/enums/streaming-sort";
import { AutomationService } from "@/shared/services/automationService";
import { Button as TextBoxButton } from "devextreme-react/text-box";
import { useEffect, useState } from "react";
import "./AutomationCardFilters.scss";
import SortByDropDownButton, {
	SortItem,
} from "@/components/SortByDropDownButton/SortByDropDownButton.tsx";
import SortDirectionButton from "@/components/SortDirectionButton/SortDirectionButton.tsx";

interface AutomationCardFiltersProps {
	onFilterChange: (filters: Record<string, any>) => void;
	initialFilters?: Record<string, any>;
	onClearFilters?: () => void;
	onClearSpecificFilter?: (fieldName: string) => void;
}

export default function AutomationCardFilters({
	onFilterChange,
	initialFilters,
	onClearFilters,
	onClearSpecificFilter,
}: AutomationCardFiltersProps) {
	const { t } = useLanguage();

	const statusOptions = [
		{ value: AutomationStatus.PENDING, label: t("automation.status.pending") },
		{ value: AutomationStatus.STARTED, label: t("automation.status.started") },
		{ value: AutomationStatus.PAUSED, label: t("automation.status.paused") },
		{
			value: AutomationStatus.FINISHED,
			label: t("automation.status.finished"),
		},
		{ value: AutomationStatus.DELETED, label: t("automation.status.deleted") },
		{ value: "ERROR", label: t("automation.error") },
	];
	const [taskTypes, setTaskTypes] = useState<
		Array<{ value: number; label: string }>
	>([]);

	const slug = (s: string) =>
		s
			.trim()
			.normalize("NFD")
			.replace(/[\p{Diacritic}]/gu, "")
			.replace(/\s+/g, "")
			.replace(/[^a-zA-Z0-9]/g, "")
			.toLowerCase();

	const [filters, setFilters] = useState<{
		statusCodes: string[];
		sessionName: string;
		taskTypeId: number[];
		technologyId: number[];
		sortBy: StreamingSortByEnum;
		sortDirection: SortDirection;
	}>(() => ({
		statusCodes:
			(initialFilters as any)?.statuses ??
			(initialFilters as any)?.statusCodes ??
			((initialFilters as any)?.statusCode
				? [(initialFilters as any).statusCode]
				: []),
		sessionName: initialFilters?.sessionName ?? "",
		taskTypeId: initialFilters?.taskTypeId
			? Array.isArray(initialFilters.taskTypeId)
				? initialFilters.taskTypeId
				: [initialFilters.taskTypeId]
			: [],
		technologyId: initialFilters?.technologyId
			? Array.isArray(initialFilters.technologyId)
				? initialFilters.technologyId
				: [initialFilters.technologyId]
			: [],
		sortBy: initialFilters?.sortBy ?? StreamingSortByEnum.STREAMING_NAME,
		sortDirection: initialFilters?.sortDirection ?? SortDirection.DESC,
	}));

	const itemsFilterDropdown = [
		{
			id: StreamingSortByEnum.STREAMING_NAME,
			text: t("itemsFilterDropdown.byStreamingName"),
		},
		{
			id: StreamingSortByEnum.DESCRIPTION,
			text: t("itemsFilterDropdown.byDescription"),
		},
		{
			id: StreamingSortByEnum.CREATED,
			text: t("itemsFilterDropdown.byCreated"),
		},
		{
			id: StreamingSortByEnum.UPDATED,
			text: t("itemsFilterDropdown.byUpdated"),
		},
		{
			id: StreamingSortByEnum.STATUS,
			text: t("itemsFilterDropdown.byStatus"),
		},
		{
			id: StreamingSortByEnum.DATASET_NAME,
			text: t("itemsFilterDropdown.byDatasetName"),
		},
	];

	const buildFilterObject = (sortByOverride?: StreamingSortByEnum) => {
		const expanded = (filters.statusCodes || []).flatMap((v) =>
			v === "ERROR" ? ["EQ1", "EQ2", "EP1"] : [v],
		);
		const dedup = Array.from(new Set(expanded));

		return {
			statuses: dedup.length ? dedup : null,
			sessionName: filters.sessionName || null,
			taskTypeId: filters.taskTypeId.length > 0 ? filters.taskTypeId : null,
			technologyId:
				filters.technologyId.length > 0 ? filters.technologyId : null,
			sortDirection: filters.sortDirection,
			sortBy: filters.sortBy,
			...(sortByOverride && { sortBy: sortByOverride }),
		};
	};

	function handleSortChange(itemData: SortItem) {
		setFilters((prev) => ({
			...prev,
			sortBy: itemData.id as StreamingSortByEnum,
		}));
	}

	function handleSortDirectionChange(sortDirection: SortDirection) {
		setFilters((prev) => ({
			...prev,
			sortDirection,
		}));
	}

	useEffect(() => {
		(async () => {
			const tts = await AutomationService.getTaskTypes(true);
			setTaskTypes(
				tts.map((tt) => ({
					value: tt.id,
					label: t(
						`automation.task-types.${slug(tt.taskTypeName)}`,
						tt.taskTypeName,
					),
				})),
			);
		})();
	}, [t]);

	const [allTechOptions, setAllTechOptions] = useState<
		Array<{ value: number; label: string }>
	>([]);

	useEffect(() => {
		(async () => {
			const all = await AutomationService.getTechnologies(true);
			setAllTechOptions(
				all.map((x) => ({
					value: x.id,
					label: t(`automation.technologies.${slug(x.name)}`, {
						defaultValue: x.name,
					}),
				})),
			);
		})();
	}, [t]);

	useEffect(() => {
		onFilterChange(buildFilterObject());
	}, [filters]);

	const getMultipleLabel = (
		opts: Array<{ value: string | number; label: string }>,
		values: Array<string | number>,
	) => {
		if (!values || values.length === 0) return "";
		if (values.length === 1) {
			return opts.find((o) => o.value === values[0])?.label ?? "";
		}
		return t("automation.selected-count", { count: values.length });
	};

	const updateFilter = (
		key: "statusCodes" | "taskTypeId" | "technologyId",
		value: any[],
	) => {
		let newValue: any;

		if (key === "statusCodes") {
			newValue = value ?? [];
		} else if (key === "taskTypeId" || key === "technologyId") {
			newValue = Array.isArray(value) ? value : [];
		}

		const hadPrev = Array.isArray((filters as any)[key])
			? ((filters as any)[key] as any[]).length > 0
			: (filters as any)[key] !== null && (filters as any)[key] !== "";

		setFilters((prev) => ({ ...prev, [key]: newValue }));

		if ((newValue as any[]).length === 0 && hadPrev && onClearSpecificFilter) {
			onClearSpecificFilter(key);
		}
	};

	const resetFilters = () => {
		setFilters({
			statusCodes: [],
			sessionName: "",
			taskTypeId: [],
			technologyId: [],
			sortBy: StreamingSortByEnum.CREATED,
			sortDirection: SortDirection.DESC,
		});
		onClearFilters?.();
	};

	return (
		<div className="row">
			{/* Estado */}
			<div className="col-lg col-12">
				<CatalogueDataGridFilter
					key={"status"}
					items={statusOptions}
					type={CatalogueSearchFilterType.status}
					keyExpr="value"
					displayExpr="label"
					columnCaption={t("catalogue.type-label")}
					currentValue={filters.statusCodes}
					onSelectionChange={(val) => updateFilter("statusCodes", val)}
					getDropDownHint={() =>
						getMultipleLabel(statusOptions, filters.statusCodes)
					}
					placeholder={t("catalogue.select-or-search")}
					selectionMode="multiple"
					label={t("automation.status-field")}
				/>
			</div>

			{/* Tipo de Tarea */}
			<div className="col-lg col-12">
				<CatalogueDataGridFilter
					key={"taskType"}
					items={taskTypes}
					type={CatalogueSearchFilterType.taskTypeId}
					keyExpr="value"
					displayExpr="label"
					columnCaption={t("catalogue.type-label")}
					currentValue={filters.taskTypeId}
					onSelectionChange={(val) => updateFilter("taskTypeId", val)}
					getDropDownHint={() =>
						getMultipleLabel(taskTypes, filters.taskTypeId)
					}
					placeholder={t("catalogue.select-or-search")}
					selectionMode="multiple"
					label={t("automation.task-type")}
				/>
			</div>

			{/* Tecnología */}
			<div className="col-lg col-12">
				<CatalogueDataGridFilter
					key={"tech"}
					items={allTechOptions}
					type={CatalogueSearchFilterType.technologyId}
					keyExpr="value"
					displayExpr="label"
					columnCaption={t("catalogue.type-label")}
					currentValue={filters.technologyId}
					onSelectionChange={(val) => updateFilter("technologyId", val)}
					getDropDownHint={() =>
						getMultipleLabel(allTechOptions, filters.technologyId)
					}
					placeholder={t("catalogue.select-or-search")}
					selectionMode="multiple"
					label={t("automation.technology")}
				/>
			</div>

			{/* Filtro de Orden por tipos */}
			<div className="col-lg-auto col-sm-auto col-12 buttons-filters ">
				<SortByDropDownButton
					items={itemsFilterDropdown}
					hint={t("catalogue.sort")}
					selectedItemKey={filters.sortBy}
					onSortSelectionChange={handleSortChange}
				/>
			</div>

			{/* Orden  ascendente y descendente */}
			<div className="col-lg-auto col-sm-auto col-12 buttons-filters">
				<SortDirectionButton
					sortDirection={filters.sortDirection}
					onSortDirectionChange={handleSortDirectionChange}
				/>
			</div>

			{/* Búsqueda por nombre de sesión */}
			<div className="col-lg col buttons-filters">
				<ItiTextBoxComponent
					value={filters.sessionName}
					textBoxAttributes={{ class: "search-field" }}
					placeholder={t("catalogue.search")}
					showClearButton={true}
					onValueChanged={(e: any) => {
						const newValue = e.value || "";
						const hadPrev = !!filters.sessionName?.length;
						setFilters((prev) => ({ ...prev, sessionName: newValue }));
						if (!newValue && hadPrev && onClearSpecificFilter)
							onClearSpecificFilter("sessionName");
					}}
				>
					<TextBoxButton
						name="search"
						location="before"
						options={{ icon: "search", stylingMode: "text" }}
					/>
				</ItiTextBoxComponent>
			</div>

			{/* Botón de Reiniciar Filtros */}
			<div className="col-lg-auto col-auto pe-0 ps-0 buttons-filters">
				<ItiButton
					className="reset-button"
					icon="mdi mdi-broom"
					hint={t("catalogue.reset-filters")}
					onClick={resetFilters}
				/>
			</div>
		</div>
	);
}
